import { useState, useEffect, Fragment } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Target, MapPin, Building2, Users, Layers, ChevronRight, ChevronDown, Info,
} from 'lucide-react';
import { getTargeting } from '../api';
import { getApiErrorMessage } from '../utils/apiError';
import { compactNumber } from '../utils/format';
import StatTile from '../components/ui/StatTile';
import SectionHeader from '../components/ui/SectionHeader';
import EmptyState from '../components/ui/EmptyState';
import { Skeleton } from '../components/ui/Skeleton';

const CURRENCY = { GBP: '£', USD: '$', EUR: '€', TRY: '₺' };

const SORTS = [
  { key: 'ads', label: 'Most ads' },
  { key: 'reach', label: 'Largest reach' },
  { key: 'city', label: 'City name' },
];

/** Horizontal age-share bars, shared by the city rows and the region cards. */
function AgeBars({ demographics, compact = false }) {
  if (!demographics?.byAge?.length) return <p className="text-ink-300 text-xs">No age data</p>;
  return (
    <div className="space-y-0.5">
      {demographics.byAge.map((a) => (
        <div key={a.ageRange} className="flex items-center gap-1.5 text-xs">
          <span className={`${compact ? 'w-11' : 'w-12'} shrink-0 text-ink-600 tabular-nums`}>{a.ageRange}</span>
          <span className="flex-1 h-1.5 rounded-full bg-ink-100 overflow-hidden">
            <span className="block h-full bg-dental-500" style={{ width: `${Math.min(100, a.share * 2.5)}%` }} />
          </span>
          <span className="w-10 text-right text-ink-500 tabular-nums">{a.share}%</span>
        </div>
      ))}
      <p className="text-ink-400 text-xs pt-0.5">
        {demographics.byGender.maleShare}% male · {demographics.byGender.femaleShare}% female
      </p>
    </div>
  );
}

function Targeting() {
  // Deep links from the clinic page arrive as ?clinic=…
  const [searchParams] = useSearchParams();
  const initialClinic = searchParams.get('clinic') || '';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sort, setSort] = useState('ads');
  const [region, setRegion] = useState('');
  const [clinic, setClinic] = useState(initialClinic);
  const [clinicInput, setClinicInput] = useState(initialClinic);
  const [expanded, setExpanded] = useState(() => new Set());

  useEffect(() => {
    document.title = 'Targeting | Dental Events';
    return () => { document.title = 'Dental Events'; };
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    const params = { sort };
    if (region) params.region = region;
    if (clinic) params.clinic = clinic;

    getTargeting(params)
      .then((res) => { if (active) { setData(res.data.data); setExpanded(new Set()); } })
      .catch((err) => { if (active) setError(getApiErrorMessage(err, 'Failed to load targeting data')); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [sort, region, clinic]);

  const toggle = (name) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });

  const summary = data?.summary;
  const regionOptions = (data?.regions || []).map((r) => r.region).filter(Boolean);

  return (
    <div className="space-y-5 sm:space-y-6 animate-fade-in-up">
      <div>
        <h1 className="page-title mb-1">Targeting</h1>
        <p className="text-sm text-ink-500">
          Where competitors actually buy their ads: every city they targeted, which age band
          they picked, and how the audience reached splits by age and gender.
          <span className="text-ink-400"> Only ads you have pulled audience data for appear here
          — use &ldquo;Update&rdquo; on the Ad Statistics page to add more.</span>
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <StatTile icon={MapPin} label="Cities targeted" value={summary?.cities ?? '—'} tone="dental" />
        <StatTile icon={Layers} label="Regions" value={summary?.regions ?? '—'} tone="accent" />
        <StatTile icon={Building2} label="Clinics" value={summary?.clinics ?? '—'} tone="blue" />
        <StatTile
          icon={Users}
          label="Ads with targeting"
          value={summary?.enrichedAdsWithTargeting ?? '—'}
          subValue={summary ? `of ${summary.totalEnrichedAds} fetched` : undefined}
          tone="amber"
        />
      </div>

      {/* Reach caveat: stated once, prominently, because it governs every number below. */}
      <div className="card bg-ink-50 ring-ink-200 flex gap-2.5">
        <Info className="w-4 h-4 text-ink-400 shrink-0 mt-0.5" />
        <p className="text-xs text-ink-600">
          An ad&apos;s reach covers every city that ad targeted, so it is shown as
          &ldquo;reach of ads including this city&rdquo; and is never divided between them —
          Facebook does not say how the audience split. Where an ad targeted one city only,
          that is flagged so you can tell the clean figures from the shared ones.
        </p>
      </div>

      <div className="card">
        <div className="flex flex-col lg:flex-row lg:flex-wrap gap-3 lg:items-end">
          <div className="lg:w-48">
            <label htmlFor="t-region" className="block text-sm font-medium text-ink-600 mb-1">Region</label>
            <select id="t-region" className="input w-full" value={region} onChange={(e) => setRegion(e.target.value)}>
              <option value="">All regions</option>
              {[...new Set(regionOptions)].sort().map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div className="flex-1 min-w-0 lg:min-w-[200px]">
            <label htmlFor="t-clinic" className="block text-sm font-medium text-ink-600 mb-1">Clinic</label>
            <input
              id="t-clinic"
              type="text"
              className="input w-full"
              placeholder="Filter by clinic…"
              value={clinicInput}
              onChange={(e) => setClinicInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && setClinic(clinicInput.trim())}
            />
          </div>
          <div className="lg:w-44">
            <label htmlFor="t-sort" className="block text-sm font-medium text-ink-600 mb-1">Sort by</label>
            <select id="t-sort" className="input w-full" value={sort} onChange={(e) => setSort(e.target.value)}>
              {SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </div>
          <button type="button" onClick={() => setClinic(clinicInput.trim())} disabled={loading} className="btn-primary whitespace-nowrap">
            {loading ? 'Loading…' : 'Apply'}
          </button>
        </div>
      </div>

      {error && (
        <div className="card bg-red-50 ring-red-200">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="space-y-2">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
      ) : !data || data.cities.length === 0 ? (
        <EmptyState
          icon={Target}
          title="No targeting data yet"
          message="Facebook only returns targeting from the per-ad endpoint. Open an event on Ad Statistics and press Update to pull it."
        />
      ) : (
        <>
          <div className="card p-0">
            <div className="overflow-x-auto rounded-2xl">
              <table className="w-full min-w-[900px] text-sm border-collapse">
                <thead>
                  <tr className="border-b border-ink-200 bg-ink-50 text-ink-600">
                    <th className="px-3 py-2.5 w-8" />
                    <th className="px-3 py-2.5 font-semibold text-left">City</th>
                    <th className="px-3 py-2.5 font-semibold text-left whitespace-nowrap">Region</th>
                    <th className="px-3 py-2.5 font-semibold text-right whitespace-nowrap">Ads</th>
                    <th className="px-3 py-2.5 font-semibold text-right whitespace-nowrap">Clinics</th>
                    <th className="px-3 py-2.5 font-semibold text-right whitespace-nowrap">Age targeted</th>
                    <th className="px-3 py-2.5 font-semibold text-right whitespace-nowrap">Biggest band</th>
                    <th className="px-3 py-2.5 font-semibold text-right whitespace-nowrap">Reach</th>
                    <th className="px-3 py-2.5 font-semibold text-right whitespace-nowrap">Est. spend</th>
                  </tr>
                </thead>
                <tbody>
                  {data.cities.map((c) => {
                    const isOpen = expanded.has(c.name);
                    return (
                      <Fragment key={c.name}>
                        <tr
                          onClick={() => toggle(c.name)}
                          className="border-b border-ink-100 hover:bg-dental-50/40 cursor-pointer transition-colors"
                        >
                          <td className="px-3 py-2.5 text-ink-400">
                            {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          </td>
                          <td className="px-3 py-2.5 font-medium text-ink-900">
                            <span className="inline-flex items-center gap-1.5">
                              <MapPin className="w-3.5 h-3.5 text-ink-400 shrink-0" />
                              {c.name}
                            </span>
                          </td>
                          <td className="px-3 py-2.5 text-ink-600 whitespace-nowrap">
                            {c.region || <span className="text-ink-300">not in reference</span>}
                          </td>
                          <td className="px-3 py-2.5 text-right tabular-nums text-ink-800">{c.adCount}</td>
                          <td className="px-3 py-2.5 text-right tabular-nums text-ink-600">{c.clinicCount}</td>
                          <td className="px-3 py-2.5 text-right whitespace-nowrap text-ink-700">
                            {c.ageMin != null ? `${c.ageMin}–${c.ageMax ?? '?'}` : '—'}
                            {c.gender ? <span className="text-ink-400"> · {c.gender}</span> : null}
                          </td>
                          <td className="px-3 py-2.5 text-right whitespace-nowrap text-ink-700">
                            {c.demographics?.topAgeRange || <span className="text-ink-300">—</span>}
                          </td>
                          <td
                            className="px-3 py-2.5 text-right tabular-nums font-semibold text-ink-900 whitespace-nowrap"
                            title={c.soloAdCount === c.adCount
                              ? 'Every ad here targeted this city alone, so the reach is this city only.'
                              : `${c.soloAdCount} of ${c.adCount} ads targeted this city alone; the rest also covered other cities.`}
                          >
                            {c.maxAdReach != null ? compactNumber(c.maxAdReach) : <span className="text-ink-300">—</span>}
                          </td>
                          <td
                            className="px-3 py-2.5 text-right tabular-nums text-ink-700 whitespace-nowrap"
                            title={summary?.cpm
                              ? `Estimated at ${summary.cpm.minCpm}–${summary.cpm.maxCpm} ${summary.cpm.currency} CPM, frequency ${summary.cpm.frequency}. The Ad Library reports no spend.`
                              : undefined}
                          >
                            {c.estimatedSpend
                              ? `${CURRENCY[c.estimatedSpend.currency] || ''}${compactNumber(c.estimatedSpend.min)}–${CURRENCY[c.estimatedSpend.currency] || ''}${compactNumber(c.estimatedSpend.max)}`
                              : <span className="text-ink-300">—</span>}
                          </td>
                        </tr>
                        {isOpen && (
                          <tr className="bg-ink-50/50">
                            <td />
                            <td colSpan={8} className="px-3 pb-3">
                              <div className="rounded-xl ring-1 ring-ink-200 bg-white p-3 grid sm:grid-cols-3 gap-4">
                                <div>
                                  <p className="text-xs font-semibold text-ink-700 mb-1.5">Age split of people reached</p>
                                  <AgeBars demographics={c.demographics} />
                                </div>
                                <div>
                                  <p className="text-xs font-semibold text-ink-700 mb-1.5">Clinics targeting here</p>
                                  <ul className="text-xs text-ink-600 space-y-0.5">
                                    {c.clinics.map((name) => (
                                      <li key={name}>
                                        <Link
                                          to={`/clinics/${encodeURIComponent(name)}`}
                                          onClick={(e) => e.stopPropagation()}
                                          className="text-blue-600 hover:text-blue-700"
                                        >
                                          {name}
                                        </Link>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                                <div className="text-xs text-ink-600 space-y-1">
                                  <p className="font-semibold text-ink-700 mb-1.5">Place</p>
                                  {c.district && <p>District: <span className="text-ink-800">{c.district}</span></p>}
                                  {c.population != null && <p>Population: <span className="text-ink-800 tabular-nums">{c.population.toLocaleString()}</span></p>}
                                  {c.dentists != null && <p>Dentists: <span className="text-ink-800 tabular-nums">{c.dentists.toLocaleString()}</span></p>}
                                  <p className="pt-1">
                                    Ads targeting only this city:{' '}
                                    <span className="text-ink-800 tabular-nums">{c.soloAdCount}/{c.adCount}</span>
                                  </p>
                                  {c.sumAdReach != null && c.adCount > 1 && (
                                    <p className="text-ink-400">
                                      {compactNumber(c.sumAdReach)} added across ads — audiences overlap
                                    </p>
                                  )}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <SectionHeader icon={Layers} title="By region" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {data.regions.map((r) => (
              <div key={r.region || 'unknown'} className="card">
                <div className="flex items-baseline justify-between mb-2">
                  <h3 className="font-semibold text-ink-900">{r.region || 'Not in reference'}</h3>
                  <span className="text-xs text-ink-500 tabular-nums">{r.adCount} ads</span>
                </div>
                <p className="text-xs text-ink-500 mb-2">
                  {r.cityCount} {r.cityCount === 1 ? 'city' : 'cities'} · {r.clinicCount}{' '}
                  {r.clinicCount === 1 ? 'clinic' : 'clinics'}
                  {r.maxAdReach != null && <> · up to {compactNumber(r.maxAdReach)} reached</>}
                </p>
                <p className="text-xs text-ink-600 mb-2 line-clamp-2" title={r.cities.join(', ')}>
                  {r.cities.join(' · ')}
                </p>
                <AgeBars demographics={r.demographics} compact />
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default Targeting;
